import { toast } from 'react-toastify'
import { ApolloError } from '@apollo/client'
import { useLocation, useNavigate } from 'react-router-dom'
import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { AuthContextData, AuthProviderProps } from './types'
import { getToken, removeToken, storeSessionToken, storeToken } from '~/utils/token'
import { AffiliatePartnerUserModel, useAffiliatePartnerGetMeLazyQuery, useLoginAffiliatePartnerUserMutation } from '~/graphql/types'
import { LoginFormData } from '~/pages/Login/components/LoginForm'

const AuthContext = createContext({} as AuthContextData)

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authUser, setAuthUser] = useState<null | AffiliatePartnerUserModel>(null)
  const [isOwner, setIsOwner] = useState(false)
  const [getUserIsLoading, setGetUserIsLoading] = useState(true)
  const [affiliatePartnerGetMe]= useAffiliatePartnerGetMeLazyQuery({
    fetchPolicy: 'no-cache'
  })
  const [loginUser, { loading: loginIsLoading }] = useLoginAffiliatePartnerUserMutation({ fetchPolicy: 'no-cache' })

  const navigate = useNavigate()
  const { pathname } = useLocation()

  const authLogin = async (formData: LoginFormData) => {
    try {
      const result = await loginUser({
        variables: {
          params: {
            password: formData.password,
            username: formData.username
          }
        }
      })

      if (result.data) {
        if(formData.rememberMe) {
          storeToken(result.data.loginAffiliatePartnerUser.accessToken)
        } else {
          storeSessionToken(result.data.loginAffiliatePartnerUser.accessToken)
        }

        await getMe()
      }
    } catch (err: any) {
      const apolloError = err as ApolloError
      const errMessage = apolloError.graphQLErrors[0]
      toast.error(errMessage?.extensions?.code === 'BAD_USER_INPUT' ? 'E-mail ou senha incorretos' : 'houve um problema ao realizar o login', { autoClose: 2000 })
    }
  }

  const getMe = useCallback(async () => {
    setGetUserIsLoading(true)
    try {
      const response = await affiliatePartnerGetMe()

      if (response.data) {
        setAuthUser(response.data.affiliatePartnerGetMe as AffiliatePartnerUserModel)
        setIsOwner(response.data.affiliatePartnerGetMe.owner)
        if(!pathname.includes('/app')) {
          navigate('/app/')
        }
      } else {
        removeToken()
      }
    } catch (err) {
      removeToken()
    } finally {
      setGetUserIsLoading(false)
    }
  }, [navigate])

  const logout = useCallback((clearStates?: () => void) => {
    if(clearStates) {
      clearStates()
    }
    removeToken()
    setAuthUser(null)
    navigate('/')
  }, [])

  useEffect(() => {
    const token = getToken()
    if (token) {
      getMe()
    }
    else {
      setGetUserIsLoading(false)
      if (pathname.includes('/app')) {
        navigate('/')
      }
    }
  }, [])

  return (
    <AuthContext.Provider value={{
      setAuthUser,
      authUser,
      getUserIsLoading,
      authLogin,
      loginIsLoading,
      logout,
      isOwner
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
